import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, CrossIcon, Preloader, RawButton, SearchInput, Text } from '@common-fe/common-fe';
import styled, { css } from 'styled-components';

import { useSelectedFundsStore } from '../stores';

import AddMoreItem from './AddMoreItem';
import { useAddMoreFunds } from './hooks';
import { useAddedFundsStore } from './stores';

interface WrapperProps {
  isModalMode?: boolean;
}
const Wrapper = styled(Box)<WrapperProps>`
  ${({ isModalMode }) => {
    if (isModalMode) {
      return css``;
    }
    return css`
      position: absolute;
      z-index: 200;
      right: 100%;
      top: -250px;
    `;
  }}
`;

const BlurWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.popupBg2};
  z-index: 100;
`;

interface TextLabelProps {
  children: React.ReactNode;
}
const TextLabel: React.FC<TextLabelProps> = ({ children }) => (
  <div>
    <Box direction="row" margin={{ left: 'xs', bottom: 'xs' }} height="24px" align="center">
      <Text size="small" color="textSecondary">
        {children}
      </Text>
    </Box>
  </div>
);

interface Props {
  onClose: () => void;
  accountId: string;
  excludeIds: string[];
  isModalMode?: boolean;
}
const AddMoreFundsContent: React.FC<Props> = ({ onClose, isModalMode, accountId, excludeIds = [] }) => {
  const addedFunds = useAddedFundsStore((state) => state.funds);
  const addedIds = useMemo(() => addedFunds.map((item) => item.id), [addedFunds]);
  const {
    data,
    total,
    isLoading,
    searchString,
    setSearchString,
    handleToggleItem,
    selectedData,
    isSearchMode,
    isSelectedMode,
  } = useAddMoreFunds({
    accountId: accountId,
    excludedIds: [...excludeIds, ...addedIds],
  });

  const { t } = useTranslation();

  const handlePushFund = useAddedFundsStore((state) => state.pushFunds);
  const handlePushSelectedFunds = useSelectedFundsStore((state) => state.pushSelectedFunds);

  const handleApply = useCallback(() => {
    if (isSelectedMode) {
      handlePushSelectedFunds(selectedData.map((fund) => fund.id));
    }
    handlePushFund(
      selectedData.map((item) => ({
        ...item,
        isHighlighted: true,
      }))
    );
    onClose();
  }, [handlePushFund, onClose, selectedData, handlePushSelectedFunds, isSelectedMode]);
  return (
    <>
      <Wrapper
        isModalMode={isModalMode}
        direction="column"
        width={isModalMode ? {} : { min: 'medium' }}
        background="canvas"
        justify="between"
        elevation={isModalMode ? undefined : 'active'}
        round="container2Round"
        data-testid="AddMoreFunds_wrapper"
      >
        <Box pad="spacing16" direction="column">
          <Box direction="row" align="center" justify="between">
            <Text data-testid="AddMoreFunds_title" size="medium" weight={700} margin={{ right: 'spacing8' }}>
              {t('Funds')}: {total}
            </Text>
            {!isModalMode && (
              <RawButton data-testid="AddMoreFunds_close-button" onClick={onClose}>
                <Box width="spacing24" height="spacing24">
                  <CrossIcon color="iconPrimary" size="14px" />
                </Box>
              </RawButton>
            )}
          </Box>
          <Box margin={{ top: 'spacing16' }}>
            <SearchInput
              autoFocus
              testId="AddMoreFunds_search"
              onChange={setSearchString}
              value={searchString}
              placeholder={t('Search')}
            />
          </Box>
        </Box>

        <Box
          direction="column"
          height="324px"
          background="module"
          pad={{ vertical: 'spacing12', horizontal: 'spacing8' }}
          border={{ size: 'small', side: 'horizontal', color: 'border2' }}
          style={{ overflow: 'auto' }}
        >
          {isLoading && (
            <Box fill>
              <Preloader testId="AddMoreFunds_preload" />
            </Box>
          )}
          {isSelectedMode && (
            <TextLabel data-testid="AddMoreFunds_count">
              {t('Selected:')} {selectedData.length}
            </TextLabel>
          )}

          {selectedData.map((item) => (
            <AddMoreItem
              key={item.id}
              link={item.link}
              logo={item.logo}
              stockCode={item.stockCode}
              value={true}
              onChangeValue={() => handleToggleItem(item.id)}
              title={item.title}
            />
          ))}
          <TextLabel data-testid="AddMoreFunds_result-text">
            {isSearchMode || !isSelectedMode ? t('Results:') : t('Others:')} {data.length}
          </TextLabel>

          {data.map((item) => (
            <AddMoreItem
              key={item.id}
              link={item.link}
              logo={item.logo}
              status={item.status}
              stockCode={item.stockCode}
              value={false}
              onChangeValue={() => handleToggleItem(item.id)}
              title={item.title}
            />
          ))}
        </Box>

        <Box direction="row" align="center" gap="spacing12" justify="center" pad={{ vertical: 'spacing16' }}>
          <AppButton testId="AddMoreFunds_cancel" onClick={onClose} buttonType="secondary" width="130px">
            {t('Cancel')}
          </AppButton>
          <AppButton testId="AddMoreFunds_submit" onClick={handleApply} width="130px">
            {t('Add')}
          </AppButton>
        </Box>
      </Wrapper>
      {!isModalMode && <BlurWrapper></BlurWrapper>}
    </>
  );
};
export default AddMoreFundsContent;
